import { Plus, X, Star, Zap } from 'lucide-react';
import { useState } from 'react';
import { inferMarketFromSymbol, normalizeSymbolInput, parseSymbolInputs, type WatchlistItem } from '@invest-copilot/shared';
import { resolveSymbolInput } from '../lib/resolveSymbol';

interface Props {
  items: WatchlistItem[];
  active: string;
  onSelect: (symbol: string) => void;
  onAdd: (item: WatchlistItem) => void;
  /** 背景解析完正確 .TW/.TWO + 公司名後，用原樂觀代號換成解析結果。 */
  onResolved: (placeholderSymbol: string, resolved: WatchlistItem) => void;
  onRemove: (symbol: string) => void;
}

/** 把 category 分成兩類：事件雷達 / 其他（含 TOP3/TOP5/智慧推荐/自選） */
function groupOf(item: WatchlistItem): 'catalyst' | 'self' {
  if (item.category === '事件雷達') return 'catalyst';
  return 'self';
}

export function Watchlist({ items, active, onSelect, onAdd, onResolved, onRemove }: Props) {
  const [adding, setAdding] = useState(false);
  const [newSymbol, setNewSymbol] = useState('');
  const parsedSymbols = parseSymbolInputs(newSymbol);

  function handleAdd(e: React.FormEvent) {
    e.preventDefault();
    if (parsedSymbols.length === 0) return;
    // 樂觀加入：先用前端推斷（.TW 預設市場）立即上 chip + 關閉輸入框，使用者秒看到結果。
    // 背景再走後端解析正確 .TW/.TWO 與公司名（上櫃股票前端推不出），回來後 patch。
    for (const sym of parsedSymbols) {
      const normalized = normalizeSymbolInput(sym);
      if (!normalized) continue;
      onAdd({
        symbol: normalized,
        market: inferMarketFromSymbol(normalized),
        name: normalized,
        category: '自選',
        emoji: '⭐',
      });
      void resolveSymbolInput(sym).then((r) => {
        if (!r) return;
        onResolved(normalized, {
          symbol: r.symbol,
          market: r.market,
          name: r.name ?? r.symbol,
          category: '自選',
          emoji: '⭐',
        });
      });
    }
    setNewSymbol('');
    setAdding(false);
  }

  const selfItems = items.filter((it) => groupOf(it) === 'self');
  const catalystItems = items.filter((it) => groupOf(it) === 'catalyst');

  return (
    <div className="space-y-2">
      {/* 自選 */}
      <Group
        icon={<Star size={12} className="text-amber-400" />}
        label="自選"
        count={selfItems.length}
        accent="border-(--border)"
      >
        {selfItems.map((it) => (
          <Chip key={it.symbol} item={it} active={active === it.symbol} onSelect={onSelect} onRemove={onRemove} variant="self" />
        ))}
        {adding ? (
          <div className="shrink-0 flex flex-col gap-1">
            <form onSubmit={handleAdd} className="flex items-center gap-1 chip">
              <input
                autoFocus
                value={newSymbol}
                onChange={(e) => setNewSymbol(e.target.value)}
                onBlur={() => !newSymbol && setAdding(false)}
                placeholder="例：2330 / 台積電 / TSLA"
                className="bg-transparent outline-none text-xs w-48 tnum"
              />
              <button
                type="submit"
                disabled={newSymbol.trim() !== '' && parsedSymbols.length === 0}
                className="text-xs text-(--accent) hover:underline disabled:opacity-40 disabled:no-underline"
              >
                加入
              </button>
            </form>
            <div className="text-[10px] text-(--text-muted) pl-1 max-w-72 leading-snug">
              {parsedSymbols.length > 0
                ? `將加入：${parsedSymbols.join(', ')}`
                : '支援數字台股自動判斷上市櫃（.TW / .TWO）、中文常用股名、逗號一次多檔'}
            </div>
          </div>
        ) : (
          <button onClick={() => setAdding(true)} className="chip border-dashed">
            <Plus size={12} />
            <span>自選</span>
          </button>
        )}
      </Group>

      {/* 事件雷達 */}
      {catalystItems.length > 0 && (
        <Group
          icon={<Zap size={12} className="text-yellow-400" />}
          label="事件雷達"
          count={catalystItems.length}
          accent="border-yellow-400/40"
        >
          {catalystItems.map((it) => (
            <Chip key={it.symbol} item={it} active={active === it.symbol} onSelect={onSelect} onRemove={onRemove} variant="catalyst" />
          ))}
        </Group>
      )}
    </div>
  );
}

function Group({
  icon, label, count, accent, children,
}: {
  icon: React.ReactNode;
  label: string;
  count: number;
  accent: string;
  children: React.ReactNode;
}) {
  return (
    <div className={`pl-2 border-l-2 ${accent}`}>
      <div className="flex items-center gap-1 mb-1 text-[10px] text-(--text-muted) uppercase tracking-wider">
        {icon}
        <span className="font-semibold">{label}</span>
        <span className="tnum">({count})</span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {children}
      </div>
    </div>
  );
}

function Chip({
  item, active, onSelect, onRemove, variant,
}: {
  item: WatchlistItem;
  active: boolean;
  onSelect: (s: string) => void;
  onRemove: (s: string) => void;
  variant: 'self' | 'top3' | 'catalyst';
}) {
  const variantCls =
    variant === 'top3' ? 'border-rose-400/40 bg-rose-400/5' :
    variant === 'catalyst' ? 'border-yellow-400/40 bg-yellow-400/5' :
    '';
  return (
    <div className="group relative shrink-0">
      <button
        onClick={() => onSelect(item.symbol)}
        className={`chip ${active ? 'active' : ''} ${variantCls}`}
        title={`${item.name} · ${item.category}`}
      >
        <span>{item.emoji ?? (variant === 'top3' ? '🔥' : variant === 'catalyst' ? '⚡' : '⭐')}</span>
        <span className="font-bold tnum">{item.symbol}</span>
        <span
          className={`text-[9px] px-1 py-0.5 rounded font-semibold ${
            item.market === 'TW' ? 'bg-amber-400/20 text-amber-300' : 'bg-sky-400/20 text-sky-300'
          }`}
        >
          {item.market}
        </span>
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); onRemove(item.symbol); }}
        className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-slate-700 hover:bg-rose-500 text-white opacity-0 group-hover:opacity-100 transition flex items-center justify-center"
        title={`移除 ${item.symbol}`}
      >
        <X size={9} />
      </button>
    </div>
  );
}
